import { useEffect, useRef, useState, type ReactNode } from "react";
import type { PresenceStatus, User } from "@majlis/protocol/types";
import { t } from "../lib/i18n.ts";

const PATHS: Record<string, string> = {
  volume: '<path d="M11 5 6 9H3v6h3l5 4V5Z"/><path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13"/>',
  micOff: '<path d="M9 9v3a3 3 0 0 0 5.1 2.1M15 9.3V6a3 3 0 0 0-5.7-1.3"/><path d="M19 11a7 7 0 0 1-1.2 3.9M5 11a7 7 0 0 0 11.4 5.4M12 18v3M3 3l18 18"/>',
  headphones: '<path d="M4 15v-3a8 8 0 0 1 16 0v3"/><rect x="3" y="14" width="4" height="6" rx="1.5"/><rect x="17" y="14" width="4" height="6" rx="1.5"/>',
  headphonesOff: '<path d="M4 15v-3a8 8 0 0 1 12.7-6.5M20 12v3"/><rect x="3" y="14" width="4" height="6" rx="1.5"/><path d="M17 17v3h2.5M3 3l18 18"/>',
  phone: '<path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2Z"/>',
  phoneOff: '<path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 1.4 2.2M14.3 15.4 15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2M3 3l18 18"/>',
  signal: '<path d="M4 20v-3M9 20v-7M14 20V9M19 20V4"/>',
  video: '<rect x="3" y="6" width="13" height="12" rx="2"/><path d="m16 10 5-3v10l-5-3"/>',
  videoOff: '<path d="M16 16v1a1 1 0 0 1-1 1H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h1M10 6h5a1 1 0 0 1 1 1v3l5-3v10M3 3l18 18"/>',
  screen: '<rect x="3" y="4" width="18" height="12" rx="2"/><path d="M8 20h8M12 16v4"/>',
  popout: '<path d="M14 4h6v6M20 4l-8 8M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5"/>',
  expand: '<path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  smile: '<circle cx="12" cy="12" r="9"/><path d="M8.5 14.5a4.5 4.5 0 0 0 7 0M9 9.5h.01M15 9.5h.01"/>',
  mic: '<rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5 11a7 7 0 0 0 14 0M12 18v3"/>',
  send: '<path d="M5 12h13M13 6l6 6-6 6"/>',
  file: '<path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/>',
  search: '<circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>',
  pin: '<path d="M12 17v5M8 3h8l-1 6 3 3H6l3-3z"/>',
  reply: '<path d="M9 14 4 9l5-5"/><path d="M4 9h10a6 6 0 0 1 6 6v4"/>',
  edit: '<path d="M4 20h4L19 9l-4-4L4 16z"/>',
  trash: '<path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3"/>',
  copy: '<rect x="8" y="8" width="12" height="12" rx="2"/><path d="M16 8V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3"/>',
  x: '<path d="M6 6l12 12M18 6 6 18"/>',
  hash: '<path d="M5 9h14M5 15h14M10 4 8 20M16 4l-2 16"/>',
  lock: '<rect x="5" y="10" width="14" height="10" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/>',
  menu: '<path d="M4 6h16M4 12h16M4 18h16"/>',
  users: '<circle cx="9" cy="8" r="3.5"/><path d="M3 20a6 6 0 0 1 12 0M16 4.5a3.5 3.5 0 0 1 0 7M21 20a6 6 0 0 0-4-5.6"/>',
  settings: '<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"/>',
  chevron: '<path d="m6 9 6 6 6-6"/>',
  check: '<path d="m5 12 5 5 9-10"/>',
  folder: '<path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>',
  link: '<path d="M10 14a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1M14 10a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1"/>',
  logout: '<path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 17l5-5-5-5M15 12H3"/>',
  download: '<path d="M12 4v12M6 10l6 6 6-6M4 20h16"/>',
  stop: '<rect x="6" y="6" width="12" height="12" rx="2"/>',
  wifi: '<path d="M2 9a15 15 0 0 1 20 0M5 12.5a10 10 0 0 1 14 0M8.5 16a5 5 0 0 1 7 0M12 19.5h.01"/>',
  bell: '<path d="M6 16V11a6 6 0 0 1 12 0v5l2 2H4zM10 21h4"/>',
  bellOff: '<path d="M3 3l18 18M8.7 5.3A6 6 0 0 1 18 11v4M6 11v5l-2 2h13M10 21h4"/>',
  at: '<circle cx="12" cy="12" r="4"/><path d="M16 12v1.5a2.5 2.5 0 0 0 5 0V12a9 9 0 1 0-3.5 7.1"/>',
  dotEmpty: '<circle cx="12" cy="12" r="1"/>',
  grip: '<circle cx="9" cy="6" r="1"/><circle cx="15" cy="6" r="1"/><circle cx="9" cy="12" r="1"/><circle cx="15" cy="12" r="1"/><circle cx="9" cy="18" r="1"/><circle cx="15" cy="18" r="1"/>',
};
export function Icon({ name, size = 20, className = "" }: { name: keyof typeof PATHS | string; size?: number; className?: string }) {
  return <svg className={`i ${className}`} width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" dangerouslySetInnerHTML={{ __html: PATHS[name] ?? "" }} />;
}

const HUES = [168, 262, 22, 212, 336, 96, 44, 190];
export const userColor = (id: string) => `hsl(${HUES[Number(BigInt(id) % 8n)]} 55% 46%)`;
export const displayName = (u: Pick<User, "displayName" | "username"> | null | undefined) => u?.displayName || u?.username || "?";

export function Avatar({ user, size = 38, status, animate }: { user: Pick<User, "id" | "displayName" | "username"> & Partial<Pick<User, "avatarUrl" | "avatarAnimatedUrl">>; size?: number; status?: PresenceStatus; animate?: boolean }) {
  const name = displayName(user);
  const [hover, setHover] = useState(false);
  const [broken, setBroken] = useState(false);
  // Animated avatars play on hover/focus only (or when the caller says so) — saves CPU and respects reduced motion.
  const src = user.avatarAnimatedUrl && (hover || animate) && !reducedMotion() ? user.avatarAnimatedUrl : user.avatarUrl;
  return (
    <span className="av" style={{ width: size, height: size, fontSize: size * 0.4, background: src && !broken ? "var(--surf2)" : userColor(user.id) }} aria-hidden="true"
      onMouseEnter={user.avatarAnimatedUrl ? () => setHover(true) : undefined} onMouseLeave={user.avatarAnimatedUrl ? () => setHover(false) : undefined}>
      {src && !broken ? <img src={src} alt="" width={size} height={size} loading="lazy" decoding="async" draggable={false} onError={() => setBroken(true)} /> : [...name][0]?.toUpperCase()}
      {status && <span className={`dot ${status}`} />}
    </span>
  );
}
const reducedMotion = () => typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Server icon: uploaded image, or the first letter of the name. */
export function ServerIcon({ server, hover }: { server: { name: string; iconUrl: string | null; iconAnimatedUrl?: string | null }; hover?: boolean }) {
  const src = server.iconAnimatedUrl && hover && !reducedMotion() ? server.iconAnimatedUrl : server.iconUrl;
  return src ? <img className="srv-img" src={src} alt="" draggable={false} /> : <>{[...server.name][0]}</>;
}

export function Modal({ title, onClose, children, wide = false, footer }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean; footer?: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>("input,textarea,select,button:not(.x)");
    first?.focus();
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    document.addEventListener("keydown", key);
    return () => { document.removeEventListener("keydown", key); prev?.focus?.(); };
  }, []);
  return (
    <div className="scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`modal${wide ? " wide" : ""}`} role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <header><h2>{title}</h2><button className="ib x" onClick={onClose} aria-label={t("close")}><Icon name="x" /></button></header>
        <div className="mbody">{children}</div>
        {footer && <footer>{footer}</footer>}
      </div>
    </div>
  );
}

export function Confirm({ title, body, danger = true, confirmLabel, onConfirm, onClose, children }:
  { title: string; body: string; danger?: boolean; confirmLabel: string; onConfirm: () => Promise<void> | void; onClose: () => void; children?: ReactNode }) {
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  return (
    <Modal title={title} onClose={onClose} footer={<>
      <button className="btn ghost" onClick={onClose}>{t("cancel")}</button>
      <button className={`btn ${danger ? "danger" : "pri"}`} disabled={busy} onClick={async () => {
        setBusy(true); setErr(null);
        try { await onConfirm(); onClose(); } catch (e) { setErr((e as Error).message); setBusy(false); }
      }}>{confirmLabel}</button></>}>
      <p className="muted">{body}</p>
      {children}
      {err && <p className="err" role="alert">{err}</p>}
    </Modal>
  );
}

/** Dropdown menu positioned under an anchor. Closes on outside click / Escape. */
export function Menu({ onClose, children, className = "" }: { onClose: () => void; children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const down = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) onClose(); };
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    setTimeout(() => document.addEventListener("mousedown", down));
    document.addEventListener("keydown", key);
    ref.current?.querySelector<HTMLElement>("button")?.focus();
    return () => { document.removeEventListener("mousedown", down); document.removeEventListener("keydown", key); };
  }, []);
  return <div className={`menu ${className}`} role="menu" ref={ref}>{children}</div>;
}

export function Field({ label, error, hint, children }: { label: string; error?: string | null; hint?: string; children: ReactNode }) {
  return (
    <label className="field">
      <span className="lbl">{label}{error && <em className="ferr"> — {error}</em>}</span>
      {children}
      {hint && !error && <small className="hint">{hint}</small>}
    </label>
  );
}

const EMOJI_SETS: Record<string, string> = {
  smileys: "😀 😂 🤣 😊 😍 🥰 😎 🤩 🤔 😅 😭 😡 🥳 😴 🤯 😬 🤗 🫡 😇 🙃 😉 😋 😜 🤪 😏 😒 🙄 😮 😱 🤒 💀 👻",
  hands: "👍 👎 👏 🙌 👋 🤝 🙏 💪 ✌️ 🤞 👌 🤌 👀 🫶",
  hearts: "❤️ 🧡 💛 💚 💙 💜 🖤 🤍 💔 💯 ✨ 🔥 ⭐ 🌙 ☀️ ⚡ 🎉 🎊",
  things: "🎮 🏆 🎯 ⚽ 🏀 🎵 🎧 📚 💻 📱 ☕ 🍕 🍔 🌴 🕌 🐪 🦅 🚀 🧠 💡 📌 ✅ ❌ ⚠️",
};
export const QUICK_REACTIONS = ["👍", "😂", "❤️", "🔥", "👀", "🙏"];

export function EmojiPicker({ onPick, onClose }: { onPick: (e: string) => void; onClose: () => void }) {
  const [tab, setTab] = useState("smileys");
  return (
    <Menu onClose={onClose} className="emoji">
      <div className="etabs">{Object.keys(EMOJI_SETS).map((k) => <button key={k} aria-pressed={tab === k} onClick={() => setTab(k)}>{EMOJI_SETS[k]!.split(" ")[0]}</button>)}</div>
      <div className="egrid">{EMOJI_SETS[tab]!.split(" ").map((e) => <button key={e} onClick={() => onPick(e)} aria-label={e}>{e}</button>)}</div>
    </Menu>
  );
}

export function Spinner() { return <span className="spinner" aria-label="…" />; }
